import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';
import { 
  Bell, Check, CheckCheck, Trash2, Clock, Calendar, 
  MessageSquare, AlertTriangle, AlertCircle, Filter
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

const NotificationsPage = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, critical

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const unreadOnly = filter === 'unread';
      const response = await api.get(`/notifications?limit=100&unread_only=${unreadOnly}`);
      let data = response.data;
      
      if (filter === 'critical') {
        data = data.filter(n => n.priority === 'critical');
      }
      
      setNotifications(data);
    } catch (error) {
      toast.error('Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  }, [api, filter]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleMarkRead = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.post('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await api.delete(`/notifications/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      toast.success('Notification deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleAcknowledge = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/acknowledge`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_acknowledged: true, is_read: true } : n)
      );
      toast.success('Notification acknowledged');
    } catch (error) {
      toast.error('Failed to acknowledge');
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      handleMarkRead(notification.id);
    }
    
    if (notification.action_url) {
      navigate(notification.action_url);
    }
  };

  const getNotificationIcon = (type, priority) => {
    if (priority === 'critical') {
      return <AlertCircle className="h-6 w-6 text-red-500" />;
    }
    
    switch (type) {
      case 'shift_assigned':
      case 'shift_accepted':
      case 'shift_declined':
      case 'shift_changed':
      case 'shift_reassigned':
        return <Calendar className="h-6 w-6 text-blue-500" />;
      case 'open_shift':
      case 'open_shift_escalation':
        return <AlertTriangle className="h-6 w-6 text-amber-500" />;
      case 'communication_new':
      case 'communication_important':
        return <MessageSquare className="h-6 w-6 text-purple-500" />;
      case 'clock_in_reminder':
      case 'clock_in_overdue':
      case 'clock_out_reminder':
      case 'clock_out_overdue':
        return <Clock className="h-6 w-6 text-green-500" />;
      default:
        return <Bell className="h-6 w-6 text-muted-foreground" />;
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'critical':
        return <Badge variant="destructive">Critical</Badge>;
      case 'important':
        return <Badge className="bg-amber-500/20 text-amber-600 border-amber-500/30">Important</Badge>;
      default:
        return null;
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader 
        title="Notifications"
        description="Stay updated with your shifts, communications, and reminders"
        actions={
          unreadCount > 0 && (
            <Button variant="outline" onClick={handleMarkAllRead}>
              <CheckCheck className="h-4 w-4 mr-2" />
              Mark All Read
            </Button>
          )
        }
      />

      {/* Filters */}
      <div className="flex gap-2">
        <Button 
          variant={filter === 'all' ? 'default' : 'outline'} 
          size="sm"
          onClick={() => setFilter('all')}
        >
          All
        </Button>
        <Button 
          variant={filter === 'unread' ? 'default' : 'outline'} 
          size="sm"
          onClick={() => setFilter('unread')}
        >
          <Bell className="h-4 w-4 mr-1" />
          Unread ({unreadCount})
        </Button>
        <Button 
          variant={filter === 'critical' ? 'default' : 'outline'} 
          size="sm"
          onClick={() => setFilter('critical')}
        >
          <AlertCircle className="h-4 w-4 mr-1" />
          Critical
        </Button>
      </div>

      {/* Notification List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      ) : notifications.length === 0 ? (
        <Card className="p-12 text-center">
          <Bell className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
          <h3 className="text-lg font-medium mb-2">No notifications</h3>
          <p className="text-muted-foreground">
            {filter === 'unread' ? "You're all caught up!" : "No notifications to display."}
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {notifications.map(notification => (
            <Card 
              key={notification.id}
              className={cn(
                "cursor-pointer transition-all hover:shadow-md",
                !notification.is_read && "border-primary/30 bg-primary/5",
                notification.priority === 'critical' && "border-red-500/50 bg-red-500/5",
                notification.priority === 'important' && "border-amber-500/30"
              )}
              onClick={() => handleNotificationClick(notification)}
            >
              <CardContent className="p-4">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 mt-1">
                    {getNotificationIcon(notification.notification_type, notification.priority)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className={cn(
                          "font-medium",
                          !notification.is_read && "font-semibold"
                        )}>
                          {notification.title}
                        </h4>
                        {getPriorityBadge(notification.priority)}
                        {!notification.is_read && (
                          <Badge variant="secondary" className="text-xs">New</Badge>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {format(new Date(notification.created_at), 'MMM d, h:mm a')}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {notification.message}
                    </p>
                    <div className="flex items-center gap-2 mt-3">
                      {notification.priority === 'critical' && !notification.is_acknowledged && (
                        <Button 
                          size="sm" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAcknowledge(notification.id);
                          }}
                        >
                          <Check className="h-4 w-4 mr-1" />
                          Acknowledge
                        </Button>
                      )}
                      {!notification.is_read && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkRead(notification.id);
                          }}
                        >
                          <Check className="h-4 w-4 mr-1" />
                          Mark Read
                        </Button>
                      )}
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-muted-foreground hover:text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(notification.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
